// DynamoDB query / update primitives used by SmartDentAction thunks. These
// functions do not dispatch; they only issue DoDB calls. Kept separate from
// the thunks so the wire-level payloads are in one place and each thunk
// stays focused on orchestration + state shape.

import { resolveLicenseProduct } from './licenseProduct';
import { DoDB, isWriteResponseError } from './ddb';
import {
    TABLE_RAYTEAMS_LICENSE,
    TABLE_RAYTEAMS_PRODUCT,
    TABLE_RAYTEAMS_PRODUCT_PACKAGE,
    skAllocationAudit,
    skAllocationAuditPrefix,
    skCorpAllocation,
    skDealerAllocation,
    skDealerAllocationPrefix,
} from './rayteamsTables';

// 주: GetManagerGroupIdByCompanyId 는 thunk 진입점에서 1회 호출되고 그 결과(managerGroupId)
// 가 query helper 들에 직접 전달된다. 호출자는 SmartDentAction.js (lib 경계 보호 + hot
// path 의 중복 scan 회피, PR #187 리뷰 Major #1).
// Fetch a single Package product (rayteams-product, sk = "info"). Null on miss.
export const fetchProduct = async (packageId) => {
    const list = await DoDB('query', {
        TableName: TABLE_RAYTEAMS_PRODUCT,
        KeyConditionExpression: '#_id = :_id and #sk = :sk',
        ExpressionAttributeNames: { '#_id': '_id', '#sk': 'sk' },
        ExpressionAttributeValues: { ':_id': packageId, ':sk': 'info' },
    });
    return list?.[0] ?? null;
};

// rayteams-product-package 의 info row — qrpath 등 QR 발급용 메타 보유.
// Dealer/Subsidiary 모두 동일 권한으로 read 가능 (queryIssuedQrsByDealer 와 동일 테이블).
export const fetchProductPackageInfo = async (packageId) => {
    const list = await DoDB('query', {
        TableName: TABLE_RAYTEAMS_PRODUCT_PACKAGE,
        KeyConditionExpression: '#_id = :_id and #sk = :sk',
        ExpressionAttributeNames: { '#_id': '_id', '#sk': 'sk' },
        ExpressionAttributeValues: { ':_id': packageId, ':sk': 'info' },
    });
    return list?.[0] ?? null;
};

// BUG-211: Allocation Summary 새로고침 1회 안에서 GetCorpAllocation/GetDealerAllocations/
// GetMyDealerAllocation/GetAllocationSubtree 가 같은 managerGroupId·dealerId 조합을 각자
// 다시 조회해 응답이 완전히 동일한 중복 호출이 반복됐다(Network 트레이스 실측: 법인탭 5회 중
// 3회, 딜러탭 7회 중 2회가 바이트 단위로 동일 응답). scopeContext.js(IMP-426)와 같은 형태의
// 짧은 TTL 캐시를 여기 세 조회 함수에 opt-in(`options.dedupe`)으로만 추가한다 — 기본값(옵션
// 미전달)은 캐시 완전 우회(기존 호출부, 특히 IssueQr/AllocateToDealer/RevokeFromDealer 의
// 예산 판정 read 는 인자를 그대로 두어 영향 없음). dedupe:true 인 호출은 추가로 `options.fresh`
// 를 받아 managerGroupResolver.js/scopeContext.js 와 같은 { fresh } 계약을 따른다 — fresh 는
// 캐시 **조회만** 건너뛰고 결과는 그대로 write-through 한다(R3 리뷰 Major — 아래 withDedupe 주석).
// BUG-211(사용자 재확인 — 실 브라우저 Network 재검토): scopeContext.js 의 기존 3초 TTL 을
// 그대로 가져왔으나, 실측 결과 부족했다. entry.expiresAt 는 조회가 "완료된 시점"부터
// 카운트되므로(진행 중엔 expiresAt=null 로 항상 hit), 문제는 응답까지 걸리는 시간이 아니라
// "먼저 끝난 조회의 3초 유예가, 늦게 도착하는 다른 조회보다 먼저 끝나버리는 것" 이다.
// GetAllocationSubtree 의 BFS 는 노드마다 조회를 순차 await 하므로(병렬 아님), 실제
// rayteams 게이트웨이 왕복 지연이 겹치면 한 클릭 안에서도 6초 이상 걸리는 것을 실측
// 확인했다 — 그 결과 GetCorpAllocation 등이 먼저 채운 캐시가 GetAllocationSubtree 가
// 도달하기 전에 만료돼 같은 클릭인데도 재조회가 발생했다. 여유를 두고 10초로 늘린다
// (write 경로는 fresh:true 로 이 캐시 자체를 건너뛰므로 여기 영향 없음).
const DEDUPE_TTL_MS = 10_000; // 같은 Refresh 클릭의 병렬 호출만 합치고, 다음 클릭엔 재조회.
const dedupeCache = new Map(); // key -> { promise, expiresAt: number|null }

// BUG-211 R3(리뷰 Security Major): DoDB 는 read 실패를 삼켜 []/null 로 resolve 한다
// (ddb.js:195-197 — READ_TYPES 는 catch 에서 항상 []) — 이 세 조회의 reject 분기는 사문이라
// 실패가 "정상 빈 결과"로 캐시되면 TTL(10초) 동안 다른 소비처까지 같은 실패를 공유한다
// (표시 selfUsable 천장 과대 fail-open). 내용 있는 결과만 캐시하고, 빈 결과는 즉시 만료시켜
// 다음 호출이 재시도하게 한다 — 그 사이 이미 진행 중인 병렬 호출(expiresAt=null 구간)은 여전히
// 공유돼 클릭 내 dedup 은 유지된다. DoDB 자체를 바꾸지 않는 이유: 모든 read 호출부가 기대는
// 공유 계약(BUG-092)이라 여기서만 우회한다.
const isCacheableResult = (result) => {
    if (Array.isArray(result)) return result.length > 0;
    return result !== null && result !== undefined;
};

// BUG-211 R3(리뷰 Architecture Major): managerGroupResolver.js/scopeContext.js 와 같은
// { fresh } 계약 — fresh 는 이미 settle 된(=stale 일 수 있는) 캐시만 건너뛰고, 결과는 그대로
// write-through 한다. 종전에는 fresh(=dedupe:false)가 캐시를 완전히 우회해 (a) 방금 쓴 값이
// 캐시에 반영되지 않아 잔여 TTL 동안 pre-write 값이 남고 (b) 같은 write-후 refresh 안의 fresh
// 호출 2건(예: GetDealerAllocations + GetAllocationSubtree root)이 서로 공유하지 못해 dedup 이
// 가장 필요한 시점에 무적용이었다.
// in-flight(expiresAt===null) 엔트리는 fresh 여도 항상 공유한다 — "지금 막 시작된 조회"는
// stale 일 수 없고, 공유하지 않으면 같은 순간에 중복 조회가 나간다(바로 이 케이스가 (b)).
const withDedupe = (key, run, { fresh = false } = {}) => {
    const cached = dedupeCache.get(key);
    if (cached && (cached.expiresAt === null || (!fresh && cached.expiresAt > Date.now()))) {
        return cached.promise;
    }
    const entry = { promise: null, expiresAt: null }; // expiresAt=null → in-flight, 항상 hit
    entry.promise = run().then(
        (result) => {
            entry.expiresAt = isCacheableResult(result) ? Date.now() + DEDUPE_TTL_MS : Date.now();
            return result;
        },
        (error) => {
            if (dedupeCache.get(key) === entry) dedupeCache.delete(key);
            throw error;
        },
    );
    dedupeCache.set(key, entry); // fresh 도 write-through — 이후 non-fresh 호출이 최신값을 받는다
    return entry.promise;
};

// 테스트 격리용 — scopeContext.js 의 clearScopeContextCache 와 같은 목적. Logout 에도 배선
// (AuthAction.js) — 계정 전환 시 이전 계정의 allocation 응답 잔존 방지(형제 캐시 2개와 동일).
export const clearAllocationQueryDedupeCache = () => dedupeCache.clear();

// rayteams-license allocation records are keyed by the license app ID
// (product.licenseAppId, SMARTDent = "smartdent_standalone"), not the product package ID. The packageId param
// is accepted for call-site compatibility but is not used for the _id lookup.
//
// BUG-093 — sk 는 managerGroupId 기반(legacy `info:{managerGroupId}`). 호출자(thunk)
// 진입점에서 1회만 resolve 하고 그 결과를 전달하도록 시그니처를 통일 (PR #187 리뷰
// Major #1 — AllocateToDealer 1회 실행에 resolve 가 3회 호출되던 hot path 회복).
// managerGroupId 가 falsy 면 DoDB 호출 없이 null/[] 반환.
export const queryCorpAllocation = async (_packageId, managerGroupId, options = {}) => {
    const product = resolveLicenseProduct(options?.product, { handler: 'smartDentQueries.queryCorpAllocation' });
    if (!product) return null;
    if (!managerGroupId) return null;
    const run = async () => {
        const list = await DoDB('query', {
            TableName: TABLE_RAYTEAMS_LICENSE,
            KeyConditionExpression: '#_id = :_id and #sk = :sk',
            ExpressionAttributeNames: { '#_id': '_id', '#sk': 'sk' },
            ExpressionAttributeValues: {
                ':_id': product.licenseAppId,
                ':sk': skCorpAllocation(managerGroupId),
            },
        });
        return list?.[0] ?? null;
    };
    if (!options.dedupe) return run();
    return withDedupe(`${product.licenseAppId}:corp:${managerGroupId}`, run, { fresh: !!options.fresh });
};

// FEAT-024 AC-9 — pagination 지원. options.limit 와 options.exclusiveStartKey
// 미전달 시 전체 일괄 로드 (기존 동작). DoDB 게이트웨이가 raw list 만 반환하면
// pagination 정보는 의미 없지만, 후속 게이트웨이 확장 대비 옵션 인터페이스만
// 일관되게 노출 — items 와 lastEvaluatedKey 분리한 객체 형태 옵션 진입로 유지.
export const queryDealerAllocations = async (_packageId, managerGroupId, options = {}) => {
    const product = resolveLicenseProduct(options?.product, { handler: 'smartDentQueries.queryDealerAllocations' });
    if (!product) return [];
    if (!managerGroupId) return [];
    const run = async () => {
        const params = {
            TableName: TABLE_RAYTEAMS_LICENSE,
            KeyConditionExpression: '#_id = :_id and begins_with(#sk, :skPrefix)',
            ExpressionAttributeNames: { '#_id': '_id', '#sk': 'sk' },
            ExpressionAttributeValues: {
                ':_id': product.licenseAppId,
                ':skPrefix': skDealerAllocationPrefix(managerGroupId),
            },
        };
        if (Number.isInteger(options.limit) && options.limit > 0) {
            params.Limit = options.limit;
        }
        if (options.exclusiveStartKey) {
            params.ExclusiveStartKey = options.exclusiveStartKey;
        }
        const list = await DoDB('query', params);
        return Array.isArray(list) ? list : [];
    };
    // pagination 요청(limit/exclusiveStartKey)은 캐시 대상에서 제외 — 페이지별로 다른 결과라
    // 키가 managerGroupId 하나뿐인 캐시에 태우면 다른 페이지 응답을 재사용하게 된다.
    const canDedupe = options.dedupe && !options.limit && !options.exclusiveStartKey;
    if (!canDedupe) return run();
    return withDedupe(`${product.licenseAppId}:dealers:${managerGroupId}`, run, { fresh: !!options.fresh });
};

export const queryDealerAllocation = async (_packageId, managerGroupId, dealerId, productArg) => {
    const product = resolveLicenseProduct(productArg, { handler: 'smartDentQueries.queryDealerAllocation' });
    if (!product) return null;
    if (!managerGroupId) return null;
    const list = await DoDB('query', {
        TableName: TABLE_RAYTEAMS_LICENSE,
        KeyConditionExpression: '#_id = :_id and #sk = :sk',
        ExpressionAttributeNames: { '#_id': '_id', '#sk': 'sk' },
        ExpressionAttributeValues: {
            ':_id': product.licenseAppId,
            ':sk': skDealerAllocation(managerGroupId, dealerId),
        },
    });
    return list?.[0] ?? null;
};

// 딜러 본인 측 조회 — 상위 법인의 managerGroupId 를 모르는 상태에서 자기 dealerId 만으로
// 할당 record 를 찾는다. sk = "info:{mgId}:{dealerId}" 형태이므로 begins_with('info:')
// + ":{dealerId}" suffix contains 로 필터. 첫 매치 반환 (dealer 가 여러 corp 에서
// 할당 받은 케이스는 일반적으로 없으나, 발생 시 첫 record 사용).
//
// 배경: 딜러의 user.companyInfo.parentCompanyId 가 실제 할당자(allocation.allocatedBy)
// 와 다른 경우가 존재 (RAY90095 의 parentCompanyId=RAY90098 vs allocation.allocatedBy=RAY90096).
// 따라서 parent 기반 corpId 추론은 부정확하므로, 딜러는 자기 dealerId 로 직접 lookup.
// BUG-211(사용자 재확인 — Network 재검토): 이 쿼리는 begins_with(sk,'info:') 로 dealerId
// 와 무관하게 테이블 전체를 스캔하고, dealerId 매칭은 client-side 에서만 한다. 첫 번째 dedupe
// 구현(캐시 키에 dealerId 포함)은 GetAllocationSubtree BFS 가 root 뿐 아니라 서브트리의 다른
// dealerId(예: 하위 딜러)에 대해서도 이 함수를 부르는 경우를 놓쳤다 — dealerId 가 다르면 캐시
// 키가 달라 스캔 자체(서버 쿼리는 dealerId 와 무관하게 동일)가 그대로 중복됐다(법인탭 실측:
// RAY99902/RAY99903 두 dealerId 로 동일 스캔이 2회). 스캔 자체를 dealerId 와 무관한 고정 키로
// 캐시하고, dealerId 별 매칭은 캐시된 리스트에서 각자 필터한다 — 서로 다른 dealerId 조회끼리도
// 스캔 1회를 공유한다.
export const queryDealerAllocationByDealerId = async (dealerId, options = {}) => {
    const product = resolveLicenseProduct(options?.product, { handler: 'smartDentQueries.queryDealerAllocationByDealerId' });
    if (!product) return null;
    if (!dealerId || typeof dealerId !== 'string') return null;
    const runScan = async () => {
        // KeyConditionExpression 만 사용 (FilterExpression 의 contains 가 게이트웨이에서 500 유발).
        // begins_with(sk, 'info:') 로 전체 allocation 조회 후 클라이언트에서 dealerId suffix 매칭.
        // SMARTDent 의 corp/dealer allocation 합쳐도 일반적으로 수백 건 이내 → Limit 500 충분.
        const list = await DoDB('query', {
            TableName: TABLE_RAYTEAMS_LICENSE,
            KeyConditionExpression: '#_id = :_id and begins_with(#sk, :prefix)',
            ExpressionAttributeNames: { '#_id': '_id', '#sk': 'sk' },
            ExpressionAttributeValues: {
                ':_id': product.licenseAppId,
                ':prefix': 'info:',
            },
            Limit: 500,
        });
        return Array.isArray(list) ? list : [];
    };
    const list = options.dedupe
        ? await withDedupe(`${product.licenseAppId}:byDealerId:__scan__`, runScan, { fresh: !!options.fresh })
        : await runScan();
    const suffix = `:${dealerId}`;
    return list.find((r) => typeof r?.sk === 'string' && r.sk.endsWith(suffix)) ?? null;
};

// Redeemed QR records (used > 0) filtered by issuer context. Writes by
// IssueQr attach managerGroupId / dealerId / companyId; pre-existing rows
// without those fields are silently excluded by the filter.
export const queryIssuerRedeemedQrs = async ({
    packageId,
    managerGroupId,
    dealerId,
    companyId,
    from,
    to,
}) => {
    // BUG-184: managerGroupId 없이 호출되면 malformed 쿼리(전체 스캔 등)로 이어질 수 있다 — 조기 차단.
    // §7 fail-closed 가드는 warn 동반(silent-drop 금지). 정상 경로(GetCustomerLicenses)는 항상 mg 전달.
    if (!managerGroupId) {
        console.warn('[guard] queryIssuerRedeemedQrs skipped — missing managerGroupId', {
            handler: 'smartDentQueries.queryIssuerRedeemedQrs',
            packageId,
            dealerId: dealerId ?? null,
            companyId: companyId ?? null,
            reason: 'managerGroupId is falsy — avoid unscoped full scan',
        });
        return [];
    }
    const filterParts = ['managerGroupId = :mgid', '#used > :zero'];
    const names = {
        '#_id': '_id',
        '#sk': 'sk',
        '#used': 'used',
    };
    const values = {
        ':_id': packageId,
        ':skPrefix': 'qr:',
        ':mgid': managerGroupId,
        ':zero': 0,
    };
    if (dealerId) {
        filterParts.push('dealerId = :did');
        values[':did'] = dealerId;
    }
    if (companyId) {
        filterParts.push('companyId = :cid');
        values[':cid'] = companyId;
    }
    // 사용(redeem) 시각 기준 기간 필터 — used 필드(Unix ms) 기반.
    // FilterExpression 은 DDB read 비용을 줄이지 못하지만 페이로드/렌더 비용은 절감.
    if (typeof from === 'number' && typeof to === 'number') {
        filterParts.push('#used BETWEEN :tsFrom AND :tsTo');
        values[':tsFrom'] = from;
        values[':tsTo'] = to;
    }
    const list = await DoDB('query', {
        TableName: TABLE_RAYTEAMS_PRODUCT_PACKAGE,
        KeyConditionExpression: '#_id = :_id and begins_with(#sk, :skPrefix)',
        FilterExpression: filterParts.join(' AND '),
        ExpressionAttributeNames: names,
        ExpressionAttributeValues: values,
    });
    return Array.isArray(list) ? list : [];
};

// FEAT-022: List all issued QR records for a dealer across all known package IDs.
// 게이트웨이(api-teams-info.rayteams.com/common/calldynamo)가 LastEvaluatedKey 를 내부
// 소진해 전량을 반환하므로 Limit 은 결과 상한이 아니라 페이지 크기다(2026-09-09 실측:
// 같은 파티션에 Limit=1/500/100000 모두 동일 건수 반환). 호출부는 전량을 받는다.
// Caller groups results by batchId for the issuance history table.
const QUERY_ISSUED_QRS_BY_DEALER_LIMIT = 500;

export const queryIssuedQrsByDealer = async (packageId, dealerId, options = {}) => {
    const { from, to, includeLedgerIssued, managerGroupId } = options;
    const filterParts = ['dealerId = :dealerId'];
    const names = { '#_id': '_id', '#sk': 'sk' };
    const values = {
        ':_id': packageId,
        ':skPrefix': 'qr:',
        ':dealerId': dealerId,
    };
    // IMP-453(#435) — 원장(2-seg) 예산 자기발급은 QR 에 dealerId 를 기록하지 않으므로(SmartDentAction
    // IssueQr 참고), 원장 소유자의 자기(self) 조회에서만 dealerId 부재 + 같은 managerGroupId 행도
    // 포함한다. 둘 다 있을 때만 확장 — 한쪽만 있으면 의미가 없다(무회귀).
    // 주의: QR 파티션(packageId)은 전역이라 managerGroupId 제약 없는 OR 은 타 테넌트 발급분까지
    // 섞일 수 있다(cross-tenant) — managerGroupId 절을 반드시 동반해야 한다.
    // F-002(#561 PR 리뷰) — mg 공유 토폴로지(BUG-208)에서는 managerGroupId 만으로 배타 경계가
    // 성립하지 않는다: receivedRecord 없는 두 회사가 각각 원장 자기발급하면 둘 다 같은 mg·
    // dealerId 부재로 적재되어 managerGroupId 단독 절은 타사 원장 QR 까지 self 조회에 반환한다
    // (CWE-200). IssueQr 이 원장 채택 시 `companyId` 를 검증된 예산 주체(effectiveCorpId)로
    // 강제 기록하도록 갱신됐으므로(F-002), 여기서도 companyId 를 배타 경계로 동반한다 —
    // `:dealerId` 값은 selfView 호출 시 자기 companyId 와 동일하므로 그대로 재사용한다.
    if (includeLedgerIssued && managerGroupId) {
        filterParts[0] = '(dealerId = :dealerId OR (attribute_not_exists(dealerId) AND managerGroupId = :mgid AND companyId = :dealerId))';
        values[':mgid'] = managerGroupId;
    }
    // 발급 시각 기준 기간 필터 — issuedAt 필드(Unix ms) 기반.
    if (typeof from === 'number' && typeof to === 'number') {
        names['#issuedAt'] = 'issuedAt';
        filterParts.push('#issuedAt BETWEEN :tsFrom AND :tsTo');
        values[':tsFrom'] = from;
        values[':tsTo'] = to;
    }
    const list = await DoDB('query', {
        TableName: TABLE_RAYTEAMS_PRODUCT_PACKAGE,
        KeyConditionExpression: '#_id = :_id and begins_with(#sk, :skPrefix)',
        FilterExpression: filterParts.join(' AND '),
        ExpressionAttributeNames: names,
        ExpressionAttributeValues: values,
        Limit: QUERY_ISSUED_QRS_BY_DEALER_LIMIT,
    });
    return Array.isArray(list) ? list : [];
};

export const queryLcuseRecord = async (appid, lcsk) => {
    const list = await DoDB('query', {
        TableName: TABLE_RAYTEAMS_LICENSE,
        KeyConditionExpression: '#_id = :_id and #sk = :sk',
        ExpressionAttributeNames: { '#_id': '_id', '#sk': 'sk' },
        ExpressionAttributeValues: { ':_id': appid, ':sk': lcsk },
    });
    return list?.[0] ?? null;
};

// Best-effort compensating decrement for a failed multi-step issuance.
// Failure here is logged but not surfaced — the caller already knows
// something went wrong and the counter drift needs operator reconciliation.
// `counterAttr` is the attribute being adjusted (typically i_initial / i_renewal
// in the issuance flow).
export const compensateQuotaDecrement = async (_packageId, allocSk, counterAttr, amount, productArg) => {
    const product = resolveLicenseProduct(productArg, { handler: 'smartDentQueries.compensateQuotaDecrement' });
    if (!product) return;
    if (!Number.isInteger(amount) || amount <= 0) return;
    try {
        await DoDB('update', {
            TableName: TABLE_RAYTEAMS_LICENSE,
            Key: { _id: product.licenseAppId, sk: allocSk },
            UpdateExpression: 'ADD #c :neg',
            ExpressionAttributeNames: { '#c': counterAttr },
            ExpressionAttributeValues: { ':neg': -amount },
        });
    } catch (error) {
        console.error('[smartDentQueries.compensateQuotaDecrement]', error);
    }
};

// FEAT-024 — allocation audit log (SPEC-RG-01 AC-3 / SPEC-RG-02 AC-7)
//
// 본사→법인 부여(grant), 법인→딜러 할당(allocate)/회수(revoke) 이력을 별도 record 로 저장.
// rayteams-product-package 테이블에 sk='audit:{corpId}:{tsPaddedMs}:{operation}:{target}' 형식으로 적재.
//
// putAllocationAudit 은 best-effort 로 동작 — allocation update 본 작업 성공 후
// audit write 가 실패해도 throw 하지 않고 console.error 만 남겨 본 작업 실패로
// 번지지 않게 한다. audit 누락은 별도 reconciliation 으로 처리되어야 하며,
// 현 단계에서는 "관측 가능한 누락" 으로만 노출 (서버 로그 + Sentry 후속 연동).
//
// item shape:
//   _id        : product.licenseAppId (SMARTDent = 'smartdent_standalone')
//   sk         : audit:{corpId}:{tsPaddedMs}:{operation}:{target}
//                — IMP-231: ts segment = 정수 ms zero-pad 13자리 (정수 정렬 안정성)
//   corpId     : 법인 ID
//   operation  : 'grant' | 'allocate' | 'revoke'
//   target     : 'self' (HQ→Corp grant) | dealerId (Corp↔Dealer)
//   delta_initial / delta_renewal : 정수, allocate=+, revoke=- 부호 부여
//   actor      : 작업자 식별자 (corpId / userId / 'hq')
//   ts         : Date.now() epoch ms (정렬·필터 보조용. sk 와 동일 값)
//   tsIso      : ISO 8601 문자열 (UI 표시용)
// IMP-130: isWriteResponseError 는 ddb.js 로 이전(상단 import). silent-success 탐지 로직 동일.

export const putAllocationAudit = async (item) => {
    if (!item || typeof item !== 'object') {
        console.warn('[putAllocationAudit] invalid item, skip');
        return false;
    }
    // PR #191 review (Security Minor) — 진단 console.log 제거. raw audit 페이로드(corpId,
    // delta_initial, actor 등) 가 production 콘솔에 노출되어 PII 누출 위험.
    try {
        // 본 프로젝트의 callDynamoV2 gateway 가 PutItem 을 silent-success 로 처리(미실행).
        // 다른 모든 write 가 UpdateItem 사용하므로 동일 패턴으로 통일.
        // 신규 레코드 생성: attribute_not_exists(#sk) ConditionExpression + SET 절로 upsert.
        // 모든 attr 에 alias 부여 (DDB reserved word — operation/target — 안전 회피).
        const response = await DoDB('update', {
            TableName: TABLE_RAYTEAMS_PRODUCT_PACKAGE,
            Key: { _id: item._id, sk: item.sk },
            UpdateExpression:
                'SET #corpId = :corpId, #op = :op, #tgt = :tgt, ' +
                '#di = :di, #dr = :dr, ' +
                '#actor = :actor, #ts = :ts, #tsIso = :tsIso',
            // 동일 sk 충돌 시 덮어쓰기 방지 — 더블클릭 idempotency.
            ConditionExpression: 'attribute_not_exists(#sk)',
            ExpressionAttributeNames: {
                '#sk': 'sk',
                '#corpId': 'corpId',
                '#op': 'operation',
                '#tgt': 'target',
                '#di': 'delta_initial',
                '#dr': 'delta_renewal',
                '#actor': 'actor',
                '#ts': 'ts',
                '#tsIso': 'tsIso',
            },
            ExpressionAttributeValues: {
                ':corpId': item.corpId,
                ':op': item.operation,
                ':tgt': item.target,
                ':di': item.delta_initial,
                ':dr': item.delta_renewal,
                ':actor': item.actor,
                ':ts': item.ts,
                ':tsIso': item.tsIso,
            },
        });
        // 200+에러 본문 케이스(SUCCESS 로 보이지만 실제로는 거절) 차단. raw response 미출력.
        if (isWriteResponseError(response)) {
            console.error('[putAllocationAudit] write REJECTED (200 with error body)', { sk: item.sk });
            return false;
        }
        return true;
    } catch (error) {
        console.error('[smartDentQueries.putAllocationAudit] FAILED', {
            sk: item.sk,
            httpStatus: error?.response?.status,
            httpStatusText: error?.response?.statusText,
            responseBody: error?.response?.data,
            errorCode: error?.code,
            errorName: error?.name,
            errorMessage: error?.message,
            stack: error?.stack?.split('\n').slice(0, 3).join('\n'),
        });
        return false;
    }
};

// 동일 corp 의 모든 audit 이벤트를 begins_with(audit:{corpId}:) 로 일괄 조회.
//
// PR #248 R2 (Arch Major): client-side ts desc resort 가 적용되지만 **page 단위로만**
// 동작. `limit`/`exclusiveStartKey` 로 pagination 사용 시 page 1 = ISO sk N건, page 2
// = INT sk M건이 분리되어 global "최신순" 깨질 수 있음 (IMP-231 마이그레이션 기간).
// **현재는 caller 가 limit 미사용 (전체 조회)** — 안전. pagination 도입 시 caller 가
// 전체 page 합산 후 다시 resort 또는 마이그레이션 완료 후 도입 권장.
//
// options:
//   - limit                : DDB Limit (페이지 크기). **마이그레이션 기간 사용 비권장**
//   - exclusiveStartKey    : DDB ExclusiveStartKey (다음 페이지 cursor). **마이그레이션 기간 사용 비권장**
//   - operation            : 단일 operation 필터 ('grant' | 'allocate' | 'revoke')
//   - operationsIn         : 다중 operation 필터 (예: ['allocate','revoke'])
//
// PR #184 R1 FYI #3 — DDB FilterExpression 으로 server-side 필터링.
// client-side 필터 + Limit 조합 시 "Limit 적용 후 client filter" 순서 때문에
// 결과가 비어 보일 수 있는 함정 해소. FilterExpression 도 Limit 후 적용되지만
// network payload 절감 + 의도가 wire-level 에 명시되는 이점.
//
// 페이징 — DoDB 게이트웨이가 LastEvaluatedKey 를 서버에서 내부 소진해 전량을 합쳐
// 반환하므로 FE 가 쫓을 다음 페이지 cursor 자체가 없다. Limit 은 결과 상한이 아니라
// 페이지 크기다(IMP-462, 2026-09-09 실측). 페이징 UI 가 필요해지면 커서가 아니라
// 전량 응답을 client-side 로 나누는 문제가 된다.
export const queryAllocationAudits = async (corpId, options = {}) => {
    const product = resolveLicenseProduct(options?.product, { handler: 'smartDentQueries.queryAllocationAudits' });
    if (!product) return [];
    const expressionValues = {
        ':_id': product.licenseAppId,
        ':skPrefix': skAllocationAuditPrefix(corpId),
    };
    const expressionNames = { '#_id': '_id', '#sk': 'sk' };
    const params = {
        // putAllocationAudit 와 동일 테이블 사용 — write 와 read 일관성.
        TableName: TABLE_RAYTEAMS_PRODUCT_PACKAGE,
        KeyConditionExpression: '#_id = :_id and begins_with(#sk, :skPrefix)',
        ExpressionAttributeNames: expressionNames,
        ExpressionAttributeValues: expressionValues,
        // 최신순. sk 가 ts 를 포함해 사전식 정렬 = 시간순 정렬.
        ScanIndexForward: false,
        // Allocate/Revoke 직후 audit write SUCCESS 인데 즉시 query 0건 보고됨 (eventual
        // consistency 의심). strongly consistent read 로 즉시 반영 보장.
        // 비용은 약 2배지만 audit 이력 즉시성 우선.
        ConsistentRead: true,
    };

    // operation FilterExpression 구성. 단일 vs 다중 분기.
    const filterParts = [];
    if (options.operation && SUPPORTED_OPERATIONS.has(options.operation)) {
        expressionNames['#op'] = 'operation';
        expressionValues[':op'] = options.operation;
        filterParts.push('#op = :op');
    } else if (Array.isArray(options.operationsIn) && options.operationsIn.length > 0) {
        const valid = options.operationsIn.filter((op) => SUPPORTED_OPERATIONS.has(op));
        if (valid.length > 0) {
            expressionNames['#op'] = 'operation';
            const placeholders = valid.map((_, i) => `:op${i}`);
            valid.forEach((op, i) => { expressionValues[`:op${i}`] = op; });
            filterParts.push(`#op IN (${placeholders.join(', ')})`);
        }
    }
    if (filterParts.length > 0) {
        params.FilterExpression = filterParts.join(' AND ');
    }

    if (Number.isInteger(options.limit) && options.limit > 0) {
        params.Limit = options.limit;
    }
    if (options.exclusiveStartKey) {
        params.ExclusiveStartKey = options.exclusiveStartKey;
    }
    const list = await DoDB('query', params);
    // PR #191 review (Code Quality Major) — 진단 raw response dump 제거. stale
    // TABLE_RAYTEAMS_LICENSE 출력은 실제 PRODUCT_PACKAGE 와 불일치하여 디버깅 혼선 유발.
    //
    // PR #248 R1 (M3): mixed-state 정렬 안전망. IMP-231 마이그레이션 기간 동안 ISO sk
    // ("2026-...") 와 정수 sk ("0001747...") 가 공존. DDB ScanIndexForward:false 사전식
    // desc 는 ISO 가 정수보다 앞 → "최신순" UI 의미 손상. record body 의 ts 정수 필드
    // 기준으로 client-side resort. 마이그레이션 완료 후 sk 단독 정렬이 정확해지면 제거 가능
    // (follow-up IMP — pagination 도입과 함께 정리).
    //
    // PR #248 R2 (CQ Minor): `?? 0` silent fallback. audit body 의 ts 는 build 시
    // padAuditTs fail-fast 로 정수 ms 보장됨 — fallback 발동 안 됨이 정상.
    // legacy malformed record (ts 누락) 가 들어와도 epoch 0 으로 최하단 정렬 → safe.
    // write path 의 fail-fast 정책과 의미상 일치: read 는 관용적 (legacy data 손상 시
    // 표시 끝으로 밀어 가시성 확보), write 는 엄격 (신규 record 무결성 보장).
    const items = Array.isArray(list) ? list : [];
    return items.slice().sort((a, b) => (b?.ts ?? 0) - (a?.ts ?? 0));
};

// audit item builder — operation 종류별 delta 부호 자동 정규화.
//   - 'grant'    : HQ→Corp 부여. tNew/tRenew 양수 그대로.
//   - 'allocate' : Corp→Dealer 할당. tNew/tRenew 양수 그대로.
//   - 'revoke'   : Corp←Dealer 회수. UI 입력은 양수지만 audit 에는 음수로 저장.
const SUPPORTED_OPERATIONS = new Set(['grant', 'allocate', 'revoke']);
export const buildAllocationAuditItem = ({
    corpId,
    operation,
    target,
    tNew = 0,
    tRenew = 0,
    actor,
    product: productArg,
}) => {
    const product = resolveLicenseProduct(productArg, { handler: 'smartDentQueries.buildAllocationAuditItem' });
    if (!product) return null;
    if (!corpId || typeof corpId !== 'string') return null;
    if (!SUPPORTED_OPERATIONS.has(operation)) return null;
    if (!target || typeof target !== 'string') return null;
    if (!Number.isInteger(tNew) || !Number.isInteger(tRenew)) return null;
    const ts = Date.now();
    const tsIso = new Date(ts).toISOString();
    const sign = operation === 'revoke' ? -1 : 1;
    // IMP-231 (2026-05-19): sk 의 ts segment 를 정수 ms 로 변경 (zero-pad 13자리).
    // record body 의 ts 정수 필드와 일관성 + 정수 정렬 안정성.
    // tsIso 필드는 record body 에 별도 유지 (가독성·legacy fallback 용).
    return {
        _id: product.licenseAppId,
        sk: skAllocationAudit(corpId, ts, operation, target),
        corpId,
        operation,
        target,
        delta_initial: sign * tNew,
        delta_renewal: sign * tRenew,
        actor: actor ?? null,
        ts,
        tsIso,
    };
};

